import type { ElementKeyframe, ElementStyle, EasingFunction } from '../types/experience';

// Easing functions
export const easingFunctions: Record<EasingFunction, (t: number) => number> = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
};

// Interpolate a numeric value
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Get accumulated style from keyframes up to a given time
export function getAccumulatedStyle(
  keyframes: ElementKeyframe[],
  upToTime: number
): Partial<ElementStyle> {
  let accumulated: Partial<ElementStyle> = {};
  for (const kf of keyframes) {
    if (kf.time <= upToTime && kf.style) {
      accumulated = { ...accumulated, ...kf.style };
    }
  }
  return accumulated;
}

// Get accumulated text from keyframes up to a given time
export function getAccumulatedText(
  keyframes: ElementKeyframe[],
  upToTime: number,
  baseText: string
): string {
  let text = baseText;
  for (const kf of keyframes) {
    if (kf.time <= upToTime && kf.text !== undefined) {
      text = kf.text;
    }
  }
  return text;
}

// Numeric properties that can be interpolated
const NUMERIC_PROPS: (keyof ElementStyle)[] = [
  'x', 'y', 'width', 'height', 'opacity', 'scale', 'rotation',
  'translateX', 'translateY', 'borderRadius', 'fontSize', 'lineHeight'
];

// Interpolate between two style objects
export function interpolateStyles(
  fromStyle: Partial<ElementStyle>,
  toStyle: Partial<ElementStyle>,
  progress: number
): Partial<ElementStyle> {
  const result: Partial<ElementStyle> = { ...fromStyle };

  for (const prop of NUMERIC_PROPS) {
    const from = fromStyle[prop] as number | undefined;
    const to = toStyle[prop] as number | undefined;
    if (from !== undefined && to !== undefined) {
      (result as Record<string, number>)[prop] = lerp(from, to, progress);
    } else if (to !== undefined && progress >= 1) {
      (result as Record<string, number>)[prop] = to;
    }
  }

  // Non-numeric properties snap at progress >= 1
  if (progress >= 1) {
    if (toStyle.backgroundColor !== undefined) result.backgroundColor = toStyle.backgroundColor;
    if (toStyle.color !== undefined) result.color = toStyle.color;
    if (toStyle.fontWeight !== undefined) result.fontWeight = toStyle.fontWeight;
    if (toStyle.textAlign !== undefined) result.textAlign = toStyle.textAlign;
    if (toStyle.visible !== undefined) result.visible = toStyle.visible;
  }

  return result;
}

// Compute interpolated style at a given playback time
export function computeKeyframeStyle(
  keyframes: ElementKeyframe[],
  playbackTime: number,
  baseStyle: Partial<ElementStyle>
): Partial<ElementStyle> {
  if (!keyframes || keyframes.length === 0) {
    return {};
  }

  const sorted = [...keyframes].sort((a, b) => a.time - b.time);

  // Find prev and next keyframes
  let prevIdx = -1;
  let nextIdx = -1;

  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].time <= playbackTime) {
      prevIdx = i;
    } else {
      nextIdx = i;
      break;
    }
  }

  // Before first keyframe: use base style
  if (prevIdx === -1) {
    if (nextIdx !== -1 && sorted[nextIdx].interpolate) {
      // Interpolate from base to first keyframe
      const next = sorted[nextIdx];
      const nextAccum = getAccumulatedStyle(sorted, next.time);
      const fromStyle = baseStyle;
      const toStyle = { ...baseStyle, ...nextAccum };
      const duration = next.time;
      if (duration <= 0) return {};
      const progress = playbackTime / duration;
      const easing = next.easing || 'linear';
      const easedProgress = easingFunctions[easing](Math.max(0, Math.min(1, progress)));
      return interpolateStyles(fromStyle, toStyle, easedProgress);
    }
    return {};
  }

  // At or past last keyframe: hold accumulated style
  if (nextIdx === -1) {
    return getAccumulatedStyle(sorted, sorted[prevIdx].time);
  }

  const prev = sorted[prevIdx];
  const next = sorted[nextIdx];

  // If next keyframe doesn't interpolate, hold at prev accumulated style
  if (!next.interpolate) {
    return getAccumulatedStyle(sorted, prev.time);
  }

  // Interpolate between prev and next accumulated styles
  const prevAccum = { ...baseStyle, ...getAccumulatedStyle(sorted, prev.time) };
  const nextAccum = { ...baseStyle, ...getAccumulatedStyle(sorted, next.time) };

  const duration = next.time - prev.time;
  if (duration <= 0) return prevAccum;

  const progress = (playbackTime - prev.time) / duration;
  const easing = next.easing || 'linear';
  const easedProgress = easingFunctions[easing](Math.max(0, Math.min(1, progress)));

  return interpolateStyles(prevAccum, nextAccum, easedProgress);
}

// Compute text at a given playback time (non-interpolated, just accumulated)
export function computeKeyframeText(
  keyframes: ElementKeyframe[],
  playbackTime: number,
  baseText: string
): string {
  if (!keyframes || keyframes.length === 0) {
    return baseText;
  }

  return getAccumulatedText(keyframes, playbackTime, baseText);
}

// Find keyframe at exact time (for editing)
export function findKeyframeAtTime(
  keyframes: ElementKeyframe[],
  time: number
): ElementKeyframe | undefined {
  return keyframes.find(kf => kf.time === time);
}

// Find keyframe index at exact time
export function findKeyframeIndexAtTime(
  keyframes: ElementKeyframe[],
  time: number
): number {
  return keyframes.findIndex(kf => kf.time === time);
}
